<template lang="pug">
.resident-in-out-schedule-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :filter-included-fields="filterFields"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：種別
    template(#cell(type)="row")
      span {{ convertTypeName(row.item) }}
    // セル：部屋用途
    template(#cell(roomUseType)="row")
      span {{ convertUseTypeName(row.item) }}
    // セル：入退所日時
    template(#cell(inOutDatetime)="row")
      span {{ convertDateText(row.item.inOutDatetime) }}（
      span(:class="getWeekClass(row.item.inOutDatetime)") {{ convertWeekText(row.item.inOutDatetime) }}
      span ）{{ convertTimeText(row.item.inOutDatetime) }}
    // セル：利用者名（部屋名）
    template(#cell(residentName)="row")
      span {{ row.item.name }}（{{ row.item.room }}）
    // セル：登録情報
    template(#cell(operation)="row")
      button.btn.edit-btn(type="button" @click="onEdit(row.item)") 修正
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { UseType } from "@/commons/services/Rooms";
import { ResidentGetModel } from "@/commons/services/Residents";

/**
 * ### 仮押さえ／転床テーブルコンポーネント
 */
@Component({})
export default class ResidentInOutScheduleTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<ResidentInOutScheduleTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<ResidentInOutScheduleTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: any;
  /** フィルター対象カラム */
  @Prop({ required: true })
  filterFields!: string[];

  get _value(): Array<ResidentInOutScheduleTableItem> {
    return this.value;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: ResidentInOutScheduleTableItem): boolean {
    if (row.groupId == this.filter) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 種別を文字列に変換する
   */
  public convertTypeName(item: ResidentInOutScheduleTableItem): string {
    if (item.type == ResidentInOutScheduleType.IN) {
      return "入所";
    } else if (item.type == ResidentInOutScheduleType.OUT) {
      return "退所";
    } else {
      return "";
    }
  }

  /**
   * ### 用途名を返す
   */
  public convertUseTypeName(item: ResidentInOutScheduleTableItem): string {
    if (item.roomUseType == UseType.UNMANAGED) {
      return "空床管理対象外";
    } else if (item.roomUseType == UseType.NORMAL_STAY) {
      return "入居用";
    } else if (item.roomUseType == UseType.SHORT_STAY) {
      return "ショートステイ";
    } else {
      return "";
    }
  }

  /**
   * ### 日付数値を表示文字列に変換する
   */
  public convertDateText(datetime: number): string {
    const date: string = this.$dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日");
    return date;
  }

  /**
   * ### 日付数値を表示文字列に変換する
   */
  public convertWeekText(datetime: number): string {
    const week: string = this.getWeekName(this.$dateUtil.convertNumberToDate(datetime));

    return week;
  }

  /**
   * ### 曜日文字列を取得する
   */
  public getWeekName(date: Date): string {
    const weekNameMap: Map<number, string> = new Map([
      [0, "日"],
      [1, "月"],
      [2, "火"],
      [3, "水"],
      [4, "木"],
      [5, "金"],
      [6, "土"]
    ]);
    const week: string | undefined = weekNameMap.get(date.getDay());

    return week ? week : "";
  }

  /**
   * ### 曜日のCSSクラスを返す
   *
   * - 日曜日、土曜日の場合は文字色指定のクラスを返す
   */
  public getWeekClass(datetime: number): string {
    const week: string = this.getWeekName(this.$dateUtil.convertNumberToDate(datetime));

    switch (week) {
      case "日": {
        return "sunday";
      }
      case "土": {
        return "saturday";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 日付数値を表示文字列に変換する
   */
  public convertTimeText(datetime: number): string {
    const time: string = this.$dateUtil.convertNumberToString(datetime, "hh：MM");

    return time;
  }

  /**
   * ### 修正ボタン押下イベント
   */
  public onEdit(item: ResidentInOutScheduleTableItem): void {
    this.$emit("onEdit", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface ResidentInOutScheduleTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface ResidentInOutScheduleTableItem extends ResidentGetModel {
  /** 種別 */
  type: ResidentInOutScheduleType;
  /** 入退所日時 */
  inOutDatetime: number;
  /** 送迎 */
  pickUpDropOffTime: string;
}

export enum ResidentInOutScheduleType {
  /** 入所 */
  IN = 0,
  /** 退所 */
  OUT = 1
}
</script>

<style lang="scss" scoped>
.resident-in-out-schedule-table-component {
  .edit-btn {
    width: 64px;
  }
}
</style>
